import { AttributeType } from './attribute-type';
import { BaseVersion } from './base/base-model';
import { ResourceError } from './base/ResourceError';
import { DeploymentValidationProfile } from './deployment-validation-profile';
import { AppComponent } from './security';
import { CredentialReferenceType, EnvironmentType, ExecutionEngine } from './dependency-map';

export interface BlueprintCollectionAttribute {
    CollectionId: string;
    CollectionEntryId: string;
    CollectionEntryAttributeId: string;
    Name: string;
    DisplayName: string;
    DefaultValue: string;
    AttributeTypeId: string;
    AttributeType: AttributeType;
    DataGeneratedBy: string;
    IsSecret: boolean;
    IsMandatory: boolean;
    IsId: boolean;
    IsParentProperty: boolean;
    ShowExpression: string;
    IsVisible: boolean;
    Value: string;
    CreatedDate: Date;
    CreatedBy?: string;
    UpdatedDate?: Date;
    UpdatedBy?: string;
    _id: string;
    Id: string;
    Active: boolean;
    DataUrlData?: any;
    DataUrl?: string;
    UseData: boolean;
    IsDropdown: boolean;
    ShowAddButton?: boolean;

    Color?: string;
    ParentName?: string;

    //Extra
    ValueFrom: number;
    IsDisableForDay2Manager: boolean;
    CloudName: string;
    CustomAttributeTypes: any[];
}

export enum ResourceSate {
    Initiated,
    PendingApproval,
    Approved,
    DeploymentInProgress,
    Operational,
    Rejected,
    Deleted,
    Error,
    SyncInProgress,
    OutOfSync,
    OperationalDrifted,
    DeleteInProgress,
    InQueue,
    DeleteInQueue,
    Locked,
    NeedsAttention,
    ErrorOnDelete,
    Skipped,
    ConflictDetected
}

export enum Action {
    ApplySource,
    ApplyTarget,
    ApplyMerged
}
export interface BlueprintCollection {
    ResourceName?: string;
    ResourceState: ResourceSate;
    ResourceStateMessage: string;
    Abbreviation?: string;
    IgnoreNamingConvention?: string;
    Abbr: string;
    DeleteTemplate: string;
    RunDeleteOnChange: boolean;
    IgnoreProvisioning: boolean;
    CollectionId: string;
    CollectionEntryId: string;
    CollectionEntryName?: string;
    IsConnectionPoint: boolean;
    CollectionName: string;
    KeyAttributeId?: any;
    RelationTypeId?: any;
    InstanceLimit: number;
    IsParent: boolean;
    IsOptional: boolean;
    ConnectionNodes?: any;
    IsSelected?: boolean;
    BlueprintCollectionAttributes: BlueprintCollectionAttribute[];
    BlueprintChildCollections: BlueprintCollection[];
    DeployedServices?: any[];
    StateDetail?: any;
    DevicePolicies? : any[];
    CreatedDate: Date;
    CreatedBy?: string;
    UpdatedDate?: Date;
    UpdatedBy?: string;
    _id: string;
    Id: string;
    Active: boolean;
    IsWorkflow?: boolean;
    ParentName?: string;
    ParentId?: string;
    RawData?: string;
    _inventoryDetail?: any;
    IsAddNewResource: boolean;
    LastMonthsCost?:any;
    Order: number;
    IsNotFill: boolean;

    //Extra
    TemplateId?: string;
    TemplateName?: string;
    SecondaryTemplateId?: string;
    ContainsDeviceConfig?: boolean;
    SourceBlueprintCollectionId?: string;
    SourceParsedInventoryId?: string;
    SourceAttachmentId?: string;
    SourceAttachmentPath?: string;
    IsTemplate?: boolean;
    ParentDependencyId?: any;
    IsShowSecondaryTemplateSelection?: boolean;
    IsCheckBoxDisabled?: boolean;
    SecondaryTemplateFilter?: any;
    TerraformResourceName?: string;
}

export interface BlueprintContainer {
    Name: string;
    Color: string;
    Border: string;
    DependencyMapId: string;
    DependencyMap?: any;
    IsSelected?: boolean;
    BlueprintCollections: BlueprintCollection[];
    CreatedDate: Date;
    CreatedBy?: string;
    UpdatedDate?: Date;
    UpdatedBy?: string;
    _id?: string;
    Id?: string;
    Active: boolean;
}

export interface BlueprintCommit {
    CreatedBy: string;
}

export interface BlueprintVariable {
    Key: string;
    Value: string;
  }

/** Links a blueprint directly to an inventory parsed from an associated device configuration. */
export interface TemplateConfigurationSource {
    TemplateId?: string;
    BlueprintCollectionId?: string;
    AttachmentId?: string;
    AttachmentPath?: string;
    ParsedInventoryId?: string;
}

export interface BluePrintList {
    Attachments: number;
    Comments: number;
    Variables: BlueprintVariable[];
    Name: string;
    Description: string;
    ApplicationIds: string[];
    selectedCard: any
    ImagePath?: string;
    BlueprintCommitHistories: BlueprintCommit[];
    BlueprintContainers: BlueprintContainer[];
    TemplateConfigurationSources?: TemplateConfigurationSource[];
    CreatedDate: string;
    CreatedBy?: string;
    UpdatedDate?: string;
    UpdatedBy?: string;
    _id: string;
    Id: string;
    Active: boolean;
    InstanceCount: number;
    Image: string;
    ApplicationUserId: string;
    Version?: any;
    State: number;
    CloudName: string;
    RecordGroupId: string;
    FormattedText: string;
    Imported: boolean;
    EnvironmentMeta: any;
    OrganizationMeta: any;
    ProjectMeta: any;
    ApprovedBy: string;
    HierarchyImage?: string;
    ConnectivityImage?: string;
    AssociationImage?: string;
    // extra
    LastCommitMessage: string;
    NewApplicationIds: string[];
    Resources: any[];
    /** Server-validated root metadata; optional only while legacy artifacts are backfilled. */
    EnvironmentType?: EnvironmentType;
    ExecutionEngine?: ExecutionEngine;
    CredentialReferenceType?: CredentialReferenceType;
    DependencyMapIds?: string[];
}

export interface BlueprintMarketplace {
    Name: string;
    Description: string;
    ImagePath?: string;
    BlueprintContainers?: BlueprintContainer[];
    CreatedDate: Date;
    CreatedBy?: string;
    UpdatedDate?: Date;
    UpdatedBy?: string;
    _id: string;
    Id: string;
    Active: boolean;
    InstanceCount: number;
    Image: string;
    ApplicationUserId: string;
    Version?: any;
    State: number;
    Price: string;
    PublishInMarketplace: boolean;
    Agreement: string;
    BlueprintReviews?: any[];
    Comments?: number;
    Tags?: any[];
    Stars: number;
    OrganizationId?: string;
    OrganizationName?: string;
    CloudName: string;
    IsPublishToMarketPlace:boolean;
}
export interface BlueprintVersion {
    Id: string;
    GroupId: string;
    CommitMessage: string;
    Version: any;
    DateTime: Date;
}

export class Inventory {
    Id?: string;
    Catalogs: InventoryCatalog[];
}

export class InventoryCatalog extends BaseVersion<string> {
    Id: string;
    AccountId: string;
    AccountName: string;
    /** Hydrated source blueprint or generated-template name for deployed-service details. */
    BlueprintName?: string;
    DeviceAuthenticationId: string;
    Errors: ResourceError[];
    Name: string;
    Description: string;
    InstanceNumber: string;
    InstanceId: number;
    selectedCard: any;
    Blueprint: BluePrintList;
    /** Mirrors the validated blueprint metadata and avoids loading distributed collections for dispatch. */
    EnvironmentType?: EnvironmentType;
    ExecutionEngine?: ExecutionEngine;
    CredentialReferenceType?: CredentialReferenceType;
    DependencyMapIds?: string[];
    LastCommitMessage: string;
    ShowPlan: boolean;
    SubmitForApproval: boolean;
    OverrideBlueprintSettings: boolean;
    OverrideVersionControlSetting: boolean;
    SkipComplianceCheck: boolean;
    OverrideDeploymentValidationProfile: boolean;
    AdvanceComplianceCheck: boolean;
    EnableLogsStream: boolean;
    GenereateTF: boolean;
    ImagePath: string;
    RecordGroupId: string;
    Price: number;
    ServiceFrom: string;
    CloudFrom: string;
    Image: any;
    PreviousVersionId: string;
    ApplicationUserId: string;
    ReviewedByUserId: string;
    LastCommitUserId: string;
    InventoryState: number;
    Version: any;
    State: number;
    CreatedDate: Date;
    ModifiedDate: Date;
    InventoryResources?: any[];
    LastSyncDate: Date;
    InventoryType: number;
    Regions?: string[];
    AppComponents: AppComponent[];
    Active: boolean;
    InventoryCommitHistories: any[];
    BlueprintSettings: any;
    BackupSettings: any;
    VersionControlSetting: any;
    dollars: any;
    Clouds: any[];
    FormattedText: string;
    Imported: boolean;
    Type: number;
    CodeFile: string;
    Cloud?: string;
    OwnersIds: any[];
    ApprovedBy?: string;
    LastMonthsCost?: any;
    DeviceInventoryIds: string[];
    DisplayBlueprintReference?: boolean;
    SourceDynamicTemplateId?: string;
    // Extra
    isSave: boolean;
    isSaveLoader: boolean;
    StateMessage: string;
    StatusDescription: string;
    IsChangedConfig: boolean;
    isInventory: boolean;
    code: string;
    DeviceName: string;
    DeviceId: string;
    IpPoolId: string;
    IpPoolName: string;
    InputParameters: any = [];
    IsWorkflow: boolean;
    LatestVersion: boolean;
    isDataCenter: boolean;
    TemplateDetails?: any = [];
    ModelType?: any;
    Tags?: any = []; 
    DeploymentValidationProfile:DeploymentValidationProfile;
    /** True while the inventory is hidden and retained only as deployment-compliance input. */
    IsPreDeployment?: boolean;
    /** Normalized assessment run that most recently evaluated this exact candidate revision. */
    PreDeploymentAssessmentRunId?: string;
    /** Server-issued hash preventing promotion after the candidate content changes. */
    PreDeploymentContentHash?: string;
    /** Raw generated IaC/configuration retained as the deployment comparison snapshot. */
    PreDeploymentSourceContent?: string;
}

export class TemplateDetails {
    BlueprintCollectionId: string;
    TemplateId: string;
    SecondaryTemplateId: string;
}


export enum InventoryType {
    'Instance' = 0,
    'Gateway' = 1,
    'Iac' = 2,
    'Connection' = 3
}

// Discuss with farjad
export enum ConnectivityInventoryType {
    'Instance' = 0,
    'TransitGateway' = 1,
    'DxGateway' = 2,
    'PureportNetwork' = 3
}

export class HistoryCatalog {
    CommitMessage: string;
    GroupId: string;
    Id: string;
    DateTime: string;
    Version: any;
}

export class BlueprintDeleteViewModel {
    Id: string;
    InventoryDeleteViewModels: InventoryDeleteViewModel[];
}

export class InventoryDeleteViewModel {
    InventoryId: string;
    DeleteOnCloud: boolean;
}

export class SyncViewModel {
    Inventory: Inventory;
    Container: BlueprintContainer;
}
